import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { custApi, formatErr } from "../../lib/customerApi";
import { toast } from "sonner";
import {
  Plus, PaperPlaneTilt, CheckCircle, XCircle, Clock, CurrencyInr,
  House, Truck, Package as PackageI, Recycle, ArrowRight, ArrowLeft,
  WarningCircle, Calendar, CaretDown, CaretUp, ShoppingBag,
  Copy, Check, ArrowsClockwise, ShieldCheck,
} from "@phosphor-icons/react";

const STATE_STYLE = {
  NEW: "bg-[#F9E4B7] text-[#7A5A11]",
  QUOTED: "bg-[#FEE2E2] text-[#7F1D1D]",
  APPROVED: "bg-[#C8E6C9] text-[#1B5E20]",
  REJECTED: "bg-[#F4C7B9] text-[#7A2E18]",
  SCHEDULED: "bg-[#BBDEFB] text-[#0D47A1]",
  IN_PROGRESS: "bg-[#F9E4B7] text-[#7A5A11]",
  COMPLETED: "bg-[#DC2626] text-white",
  CANCELLED: "bg-[#E5E2D9] text-[#5C5A55]",
};
const PRIORITY_STYLE = {
  NORMAL: "bg-[#FEE2E2] text-[#DC2626]",
  HIGH: "bg-[#F9E4B7] text-[#7A5A11]",
  URGENT: "bg-[#F4C7B9] text-[#7A2E18]",
  EMERGENCY: "bg-[#F4C7B9] text-[#7A2E18]",
  LOW: "bg-[#F7F6F2] text-[#8A8780] border border-[#E5E2D9]",
};

/**
 * Strip the "Select-all" marker we prepend to the notes/description when the
 * customer picks 'Any of these dates'. Removes the bracket block AND its
 * trailing 'Admin will notify …' sentence in a single regex sweep.
 */
function _stripAnyOfMarker(text) {
  if (!text) return text;
  // Match either (a) a leading newline + marker line, or (b) the marker at
  // the very start of the string. Preserves the newline that follows the
  // marker so surrounding lines stay joined correctly.
  return text
    .replace(/(?:\n|^)[^\n]*\[Any of:[^\]]*\][^\n]*/g, "")
    .trim();
}


// Bag-request specific status styling (independent from SR statuses so
// we don't accidentally reuse APPROVED green for a customer bag request
// that's still awaiting fulfilment).
const BAG_STATUS_STYLE = {
  pending: "bg-[#F9E4B7] text-[#7A5A11]",
  approved: "bg-[#DDE9F6] text-[#0D47A1]",
  fulfilled: "bg-[#C8E6C9] text-[#1B5E20]",
  rejected: "bg-[#F4C7B9] text-[#7A2E18]",
  cancelled: "bg-[#E5E2D9] text-[#5C5A55]",
};

// SR statuses that count as "history" — anything terminal or explicitly
// closed. Everything else (NEW / QUOTED / APPROVED / SCHEDULED /
// IN_PROGRESS + `requires_admin_confirmation`) stays active.
const SR_HISTORY_STATES = new Set([
  "COMPLETED", "CANCELLED", "REJECTED", "DELETED",
]);
// Bag-request statuses considered "history": fulfilled (bags received),
// rejected, or cancelled. `pending` and `approved` still ride the top
// section because they need customer attention (share PIN / pay).
const BAG_HISTORY_STATES = new Set(["fulfilled", "rejected", "cancelled"]);

export default function MeRequests() {
  const [srs, setSrs] = useState([]);
  const [bags, setBags] = useState([]);
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Iter 70 — pull-to-refresh state. `pull` is the current drag delta
  // in px (0 = idle); `refreshing` gates concurrent reloads. Mobile
  // touch handlers below drive both.
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const scrollerRef = useRef(null);
  const PTR_THRESHOLD = 70;   // px pull required to trigger reload
  const PTR_MAX = 110;         // clamp so the indicator never overshoots

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        custApi.get("/me/service-requests"),
        custApi.get("/me/bag-requests").catch(() => ({ data: [] })),
      ]);
      setSrs(a.data || []);
      setBags(b.data || []);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Pull-to-refresh handlers — attached to the scroll container. Only
  // engage when the container is at scrollTop=0 so we don't hijack a
  // mid-scroll gesture. Uses passive listeners for the initial touch
  // to keep scroll snappy.
  const onTouchStart = (e) => {
    if (refreshing) return;
    const scr = scrollerRef.current;
    if (scr && scr.scrollTop > 0) return;
    startY.current = e.touches[0].clientY;
  };
  const onTouchMove = (e) => {
    if (startY.current == null || refreshing) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta <= 0) { setPull(0); return; }
    // Rubber-band: divide by 1.6 for physical feel
    const clamped = Math.min(delta / 1.6, PTR_MAX);
    setPull(clamped);
    if (delta > 4) e.preventDefault();  // stop rubber-band bounce
  };
  const onTouchEnd = async () => {
    if (refreshing) return;
    const p = pull;
    startY.current = null;
    if (p >= PTR_THRESHOLD) {
      setRefreshing(true);
      setPull(PTR_THRESHOLD);
      try { await load(); toast.success("Refreshed"); }
      finally { setRefreshing(false); setPull(0); }
    } else {
      setPull(0);
    }
  };

  const { activeSrs, historySrs, activeBags, historyBags } = useMemo(() => {
    return {
      activeSrs: srs.filter(r => !SR_HISTORY_STATES.has(r.status)),
      historySrs: srs.filter(r => SR_HISTORY_STATES.has(r.status)),
      activeBags: bags.filter(r => !BAG_HISTORY_STATES.has(r.status)),
      historyBags: bags.filter(r => BAG_HISTORY_STATES.has(r.status)),
    };
  }, [srs, bags]);

  const activeCount = activeSrs.length + activeBags.length;
  const historyCount = historySrs.length + historyBags.length;
  const showPtrIndicator = pull > 0 || refreshing;
  const ptrProgress = Math.min(pull / PTR_THRESHOLD, 1);

  return (
    <div
      ref={scrollerRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      className="space-y-4 relative touch-pan-y px-4 py-4"
      style={{ transform: pull ? `translateY(${pull}px)` : undefined,
                transition: refreshing || pull === 0 ? "transform 240ms ease" : "none" }}
      data-testid="me-requests">
      {/* Pull-to-refresh indicator — hovers above the header while
          the finger is down. Refreshing state shows a spinner. */}
      {showPtrIndicator && (
        <div className="absolute left-0 right-0 -top-10 flex items-center justify-center"
              style={{ opacity: refreshing ? 1 : ptrProgress }}
              data-testid="mr-ptr-indicator">
          <div className="h-9 w-9 rounded-full bg-white border border-[#E5E2D9] flex items-center justify-center shadow-sm">
            <ArrowsClockwise
              size={14}
              weight={refreshing ? "bold" : "regular"}
              className={refreshing ? "animate-spin text-[#DC2626]" : "text-[#5C5A55]"}
              style={{ transform: refreshing ? undefined : `rotate(${ptrProgress * 320}deg)` }}
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Requests</div>
          <div className="font-heading text-2xl">My requests</div>
        </div>
        <button onClick={() => setOpen(true)} data-testid="mr-new"
                className="h-10 px-3 inline-flex items-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-medium">
          <Plus size={14}/>New Request
        </button>
      </div>

      {/* ---------- ACTIVE ---------- */}
      <section data-testid="mr-active-section">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#DC2626] font-semibold mb-2 flex items-center gap-1.5">
          <Clock size={11} weight="fill"/>Active · {activeCount}
        </div>
        {activeCount === 0 ? (
          <div className="bg-white border border-[#E5E2D9] rounded-md p-8 text-center text-sm text-[#8A8780]" data-testid="mr-empty">
            <PaperPlaneTilt size={26} className="inline-block mb-2 opacity-40"/><br/>
            No active requests. Tap <strong>New Request</strong> above to book a pickup or order bags.
          </div>
        ) : (
          <div className="space-y-2">
            {activeSrs.map(r => <SrRow key={`sr-${r.id}`} r={r}/>)}
            {activeBags.map(r => <BagRow key={`br-${r.id}`} r={r}/>)}
          </div>
        )}
      </section>

      {/* ---------- HISTORY (collapsed by default) ---------- */}
      {historyCount > 0 && (
        <section data-testid="mr-history-section">
          <button onClick={() => setShowHistory(s => !s)}
                  data-testid="mr-history-toggle"
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-[#F7F6F2] hover:bg-[#EFEDE4] border border-[#E5E2D9] rounded-md text-sm">
            <span className="inline-flex items-center gap-1.5 text-[#5C5A55]">
              <PackageI size={12}/>Request history · {historyCount}
            </span>
            {showHistory ? <CaretUp size={12} weight="bold"/> : <CaretDown size={12} weight="bold"/>}
          </button>
          {showHistory && (
            <div className="space-y-2 mt-2" data-testid="mr-history-list">
              {historySrs.map(r => <SrRow key={`sr-${r.id}`} r={r}/>)}
              {historyBags.map(r => <BagRow key={`br-${r.id}`} r={r}/>)}
            </div>
          )}
        </section>
      )}

      {open && <CreateRequestWizard onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }}/>}
    </div>
  );
}

// -----------------------------------------------------------------
// Row renderers — separate components so SR vs bag-request rows stay
// self-contained. Test IDs mr-sr-{id} and mr-bag-{id} let QA target
// them directly.
// -----------------------------------------------------------------

const SrRow = ({ r }) => {
  // Iter 203 — richer list card: shows package + preferred date +
  // amount + payment pill inline, so the customer sees everything
  // important without opening the detail view.
  const paid = r.paid_upfront || r.payment_status === "paid";
  const awaiting = !paid && r.razorpay_order_id;
  return (
    <Link to={`/me/tracking/${r.id}`}
          className="block bg-white border border-[#E5E2D9] rounded-xl p-3 hover:border-[#DC2626] hover:shadow-md transition-all"
          data-testid={`mr-sr-${r.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#FEE2E2] to-[#FCA5A5] flex items-center justify-center shrink-0">
          <Truck size={18} color="#7F1D1D" weight="fill"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-[11px] text-[#DC2626] font-bold"
                   data-testid={`mr-sr-no-${r.id}`}>{r.request_no}</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold ${STATE_STYLE[r.status] || "bg-[#E5E2D9]"}`}>{r.status}</span>
            {r.priority && r.priority !== "NORMAL" && (
              <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold ${PRIORITY_STYLE[r.priority] || "bg-[#E5E2D9]"}`}>{r.priority}</span>
            )}
            {paid && (
              <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-[#C8E6C9] text-[#1B5E20] font-semibold inline-flex items-center gap-1"
                     data-testid={`mr-sr-paid-${r.id}`}>
                <CheckCircle size={9} weight="fill"/>Paid
              </span>
            )}
            {awaiting && (
              <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-[#FEE9C0] text-[#7A5A11] font-semibold">
                Awaiting pay
              </span>
            )}
            {r.requires_admin_confirmation && (
              <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-[#F4C7B9] text-[#7A2E18]">
                <WarningCircle size={9} className="inline mr-0.5"/>Admin review
              </span>
            )}
          </div>
          <div className="text-sm font-medium mt-1 truncate text-[#1A1A1A]">{r.package_name || "Package pending"}</div>
          <div className="text-[11px] text-[#5C5A55] mt-0.5 flex items-center gap-1.5">
            <Calendar size={10}/>
            {r.preferred_date
              ? new Date(r.preferred_date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
              : "Date pending"}
            {r.preferred_slot && <span> · {r.preferred_slot}</span>}
            {r.scheduled_date && r.scheduled_date !== r.preferred_date && (
              <span> · <strong>{r.scheduled_date}</strong></span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          {(r.payable_total != null || r.quote_amount) && (
            <div className="font-mono text-sm font-semibold text-[#1A1A1A]">
              ₹{Number(r.payable_total ?? r.quote_amount).toFixed(0)}
            </div>
          )}
          <ArrowRight size={14} className="text-[#8A8780] ml-auto mt-1"/>
        </div>
      </div>
    </Link>
  );
};

const COLOR_SWATCH = {
  yellow: "#F5C518", blue: "#1E5FA0", red: "#C0392B",
  white: "#F4F4F4", black: "#1A1A1A", grey: "#7A7A7A",
};

const BagRow = ({ r }) => {
  const paid = r.payment_status === "paid";
  const needsPay = r.payment_mode === "online" && !paid && r.payment_link_url && r.status !== "cancelled" && r.status !== "rejected";
  const showPin = r.delivery_pin && r.status !== "fulfilled" && r.status !== "rejected" && r.status !== "cancelled";
  // Iter 70 — Copy PIN affordance. Falls back gracefully when the
  // Clipboard API is unavailable (older WebViews) via a hidden
  // textarea + execCommand trick.
  const [copied, setCopied] = useState(false);
  const copyPin = async () => {
    if (!r.delivery_pin) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(r.delivery_pin);
      } else {
        const ta = document.createElement("textarea");
        ta.value = r.delivery_pin;
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select(); document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("PIN copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Couldn't copy — long-press to copy manually.");
    }
  };
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-4"
          data-testid={`mr-bag-${r.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#F9E4B7] flex items-center justify-center shrink-0">
          <ShoppingBag size={16} color="#7A5A11" weight="fill"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-[0.18em] text-[#8A8780]">Bag order</span>
            <span className="font-mono text-[11px] text-[#7A5A11]"
                  data-testid={`mr-bag-no-${r.id}`}>{r.request_no || r.id.slice(0, 8)}</span>
            <span className={`text-[9px] px-2 py-0.5 rounded uppercase tracking-wider ${BAG_STATUS_STYLE[r.status] || "bg-[#E5E2D9]"}`}>{r.status}</span>
            {paid && <span className="text-[9px] px-2 py-0.5 rounded uppercase tracking-wider bg-[#C8E6C9] text-[#1B5E20]">Paid</span>}
            {!paid && r.payment_mode === "cod" && r.status !== "fulfilled" && (
              <span className="text-[9px] px-2 py-0.5 rounded uppercase tracking-wider bg-[#F7F6F2] text-[#5C5A55]">COD</span>
            )}
            {!paid && r.payment_mode === "online" && r.status !== "fulfilled" && (
              <span className="text-[9px] px-2 py-0.5 rounded uppercase tracking-wider bg-[#F4C7B9] text-[#7A2E18]">Unpaid</span>
            )}
          </div>
          <div className="text-sm mt-1 flex items-center gap-1.5">
            {r.colour_code && (
              <span className="w-3 h-3 rounded-full border border-black/10"
                    style={{ background: COLOR_SWATCH[r.colour_code] || "#7A7A7A" }}/>
            )}
            {r.quantity} × {r.waste_category}{r.colour_code ? ` · ${r.colour_code.toUpperCase()}` : ""}
          </div>
          <div className="text-[11px] text-[#8A8780]">
            {r.total_amount != null && <>₹{Number(r.total_amount).toFixed(2)} · </>}
            {r.created_at ? new Date(r.created_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {showPin && (
              <div className="inline-flex items-center gap-1.5 bg-[#F9F0D6] border border-[#F5C518]/60 rounded px-2 py-1"
                    data-testid={`mr-bag-pin-${r.id}`}>
                <span className="text-[9px] uppercase tracking-[0.16em] text-[#7A5A11] font-semibold">PIN</span>
                <span className="font-mono text-sm text-[#1A1A1A] tracking-[0.25em]">{r.delivery_pin}</span>
                <button type="button"
                        onClick={copyPin}
                        data-testid={`mr-bag-pin-copy-${r.id}`}
                        aria-label={copied ? "PIN copied" : "Copy PIN"}
                        className="ml-0.5 h-6 w-6 grid place-items-center rounded hover:bg-[#F5C518]/25 text-[#7A5A11] transition-colors">
                  {copied
                    ? <Check size={12} weight="bold" className="text-[#1B5E20]" />
                    : <Copy size={12} weight="regular" />}
                </button>
              </div>
            )}
            {needsPay && (
              <a href={r.payment_link_url} target="_blank" rel="noopener noreferrer"
                  data-testid={`mr-bag-pay-${r.id}`}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-medium">
                <CurrencyInr size={11}/>Pay now
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// CREATE REQUEST WIZARD — 4 steps
// 1. Service Type (Home / Drop)
// 2. Priority (Normal / High / Urgent)
// 3. Waste Category → Package
// 4. Date (Normal only) + Address + Notes
// =================================================================
function CreateRequestWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  // Iter 201 · Show BOTH Confirm and Pay-and-Book buttons on Step 3
  // regardless of the tenant feature flag, since minimum ₹1000 wallet
  // balance guard now protects the booking flow either way.
  const MIN_WALLET_BALANCE = 1000;
  const [walletBalance, setWalletBalance] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    custApi.get("/me/wallet")
      .then(r => { if (alive) setWalletBalance(Number(r.data?.balance || 0)); })
      .catch(() => { if (alive) setWalletBalance(0); })
      .finally(() => { if (alive) setWalletLoading(false); });
    return () => { alive = false; };
  }, []);
  const walletOK = walletBalance != null && walletBalance >= MIN_WALLET_BALANCE;
  // Iter 200 · load Razorpay Standard Checkout script once.
  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const submitAndPay = async () => {
    if (!f.package_id) { toast.error("Pick a package"); return; }
    // Iter 202 · Multi-date path validation.
    if (multiDateMode) {
      if (multiDates.length < 2) {
        toast.error("Pick at least 2 dates for multi-date booking");
        return;
      }
    } else if (!isHighOrEmergency && !f.preferred_date) {
      toast.error("Pick a service date"); return;
    }
    // Iter 201 · minimum wallet balance guard.
    if (!walletOK) {
      toast.error(
        `Minimum wallet balance of ₹${MIN_WALLET_BALANCE} required to book. `
        + `You have ₹${(walletBalance || 0).toFixed(0)}. Top up from Wallet.`
      );
      return;
    }
    setBusy(true);
    let created = null;      // single-date: SR row · multi-date: { booking_group_id, service_requests }
    let orderEndpoint = null;
    let verifyEndpoint = null;
    let describe = "";
    let poller = null;       // /refresh-payment fallback if the tab closes before verify
    try {
      if (multiDateMode) {
        // Iter 202 · Multi-book — creates N SRs and one Razorpay Order.
        const base = buildSrPayload();
        const payload = {
          service_category_type: base.service_category_type,
          waste_category: base.waste_category,
          service_category: base.service_category,
          address: base.address,
          description: base.description,
          package_id: base.package_id,
          photos: base.photos,
          extra_charge_ids: base.extra_charge_ids,
          preferred_slot: base.preferred_slot,
          dates: [...multiDates].sort(),
        };
        const r = await custApi.post("/me/service-requests/multi-book", payload);
        created = r.data;
        // Iter 202 fix · surface any partial failures so the customer
        // isn't silently charged for fewer dates than they picked.
        if (created.failed_count > 0) {
          const missed = (created.errors || [])
            .map(e => `${e.date} (${e.error})`).join(", ");
          toast.warning(
            `${created.created_count} of ${created.created_count + created.failed_count} dates booked — skipped: ${missed}`,
            { duration: 6000 },
          );
        } else {
          toast.success(`${created.created_count} requests created`);
        }
        orderEndpoint = `/me/service-requests/group/${created.booking_group_id}/razorpay-order`;
        verifyEndpoint = `/me/service-requests/${created.service_requests[0].id}/razorpay-verify`;
        poller = `/me/service-requests/group/${created.booking_group_id}/refresh-payment`;
        describe = `Pay & Book · ${created.created_count} dates`;
      } else {
        const payload = { ...buildSrPayload(), paid_upfront: true };
        const r = await custApi.post("/me/service-requests", payload);
        created = r.data;
        toast.success(`Request created · ${created.request_no}`);
        orderEndpoint = `/me/service-requests/${created.id}/razorpay-order`;
        verifyEndpoint = `/me/service-requests/${created.id}/razorpay-verify`;
        poller = `/me/service-requests/${created.id}/refresh-payment`;
        describe = `Pay & Book · ${created.request_no}`;
      }
      const ord = await custApi.post(orderEndpoint);
      const orderData = ord.data;
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error("Payment SDK failed to load. You can pay later from the request.");
        onCreated?.(created); onClose?.();
        setBusy(false); return;
      }
      const rzp = new window.Razorpay({
        key: orderData.key_id,
        amount: orderData.amount, currency: orderData.currency,
        name: "Rescigo", description: describe,
        order_id: orderData.order_id,
        prefill: orderData.prefill,
        theme: { color: "#0B2B66" },
        handler: async (resp) => {
          try {
            await custApi.post(verifyEndpoint, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment received · slot confirmed");
          } catch (err) {
            // Verify path failed — fall back to the server-side
            // reconciler in case Razorpay already captured the payment.
            try {
              const rec = await custApi.post(poller);
              if (rec.data?.reconciled) {
                toast.success("Payment received · slot confirmed");
              } else {
                toast.error(`Payment verify failed: ${formatErr(err)}`);
              }
            } catch {
              toast.error(`Payment verify failed: ${formatErr(err)}`);
            }
          } finally {
            onCreated?.(created); onClose?.();
          }
        },
        modal: {
          ondismiss: async () => {
            // Iter 202 · Poll the server once — if the customer paid
            // but the handler never fired (tab focus lost, redirected
            // to bank), the /refresh-payment endpoint will find the
            // captured payment and flip the SR(s) to paid.
            try {
              const rec = await custApi.post(poller);
              if (rec.data?.reconciled) {
                toast.success("Payment received · slot confirmed");
              } else {
                toast.info("Payment cancelled — your request is unpaid. Pay later from My Requests.");
              }
            } catch {
              toast.info("Payment cancelled — pay later from My Requests.");
            }
            onCreated?.(created); onClose?.();
          },
        },
      });
      rzp.on("payment.failed", (evt) => {
        toast.error(`Payment failed: ${evt?.error?.description || "unknown error"}`);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatErr(e));
      if (created) { onCreated?.(created); onClose?.(); }
    } finally {
      setBusy(false);
    }
  };
  const [me, setMe] = useState(null);
  const [charges, setCharges] = useState({});
  const [categories, setCategories] = useState([]);
  const [dateOptions, setDateOptions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [pkgLoading, setPkgLoading] = useState(false);

  const [f, setF] = useState({
    service_category_type: "home_service",
    priority: "NORMAL",
    waste_category: "D-BMW",  // Default to D-BMW per product requirement
    package_id: "",
    preferred_date: "",
    address: "",
    description: "",
    extra_charge_ids: [],
  });
  // Iter 202 · Multi-date Pay & Book — customer picks 2-6 dates in one
  // go, we mint a single Razorpay order for the combined amount and
  // create N SRs linked by booking_group_id.
  const [multiDateMode, setMultiDateMode] = useState(false);
  const [multiDates, setMultiDates] = useState([]);
  const [extraCharges, setExtraCharges] = useState([]); // optional add-ons
  const [bill, setBill] = useState(null);
  const [zoneSnap, setZoneSnap] = useState(null);
  const [altLocations, setAltLocations] = useState([]);
  const [specialDates, setSpecialDates] = useState([]);   // upcoming rules for this zone
  // Iter 202 · dates the customer already has an active booking on
  // (same package). Used to grey these out in the multi-date grid so
  // the customer doesn't hit the duplicate-guard 400 mid-flow.
  const [bookedDates, setBookedDates] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const [meR, ch, cats, dates, ec, zs, alt, sp, my] = await Promise.all([
          custApi.get("/me"),
          custApi.get("/me/extra-charges"),
          custApi.get("/me/waste-categories"),
          custApi.get("/me/next-collection-dates"),
          custApi.get("/me/extra-charges-list").catch(() => ({ data: [] })),
          custApi.get("/me/zone-snapshot").catch(() => ({ data: null })),
          custApi.get("/me/alternate-locations?days=14").catch(() => ({ data: { items: [] } })),
          custApi.get("/me/special-collection-dates").catch(() => ({ data: { rules: [] } })),
          custApi.get("/me/service-requests").catch(() => ({ data: [] })),
        ]);
        setMe(meR.data);
        setF(s => ({ ...s, address: meR.data?.address || "" }));
        setCharges(ch.data || {});
        setCategories(cats.data || []);
        setDateOptions(dates.data?.dates || []);
        setExtraCharges(ec.data || []);
        setZoneSnap(zs.data);
        setAltLocations(alt.data?.items || []);
        setSpecialDates(sp.data?.rules || []);
        // Iter 202 · build the set of already-booked dates from active
        // SRs so the multi-date grid can disable them.
        const active = ["NEW", "QUOTED", "APPROVED", "SCHEDULED", "IN_PROGRESS"];
        setBookedDates(new Set(
          (my.data || [])
            .filter(x => active.includes(x.status) && x.preferred_date)
            .map(x => x.preferred_date)
        ));
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, []);

  // Filter packages by waste_category AND service_category_type (server-side)
  useEffect(() => {
    if (!f.waste_category) { setPackages([]); return; }
    setPkgLoading(true);
    const params = new URLSearchParams();
    params.set("waste_category", f.waste_category);
    params.set("service_category_type", f.service_category_type);
    custApi.get(`/me/packages?${params.toString()}`)
      .then(r => setPackages(r.data || []))
      .catch(e => toast.error(formatErr(e)))
      .finally(() => setPkgLoading(false));
  }, [f.waste_category, f.service_category_type]);

  // Live bill preview
  useEffect(() => {
    if (!f.package_id) { setBill(null); return; }
    custApi.post("/me/preview-bill", {
      package_id: f.package_id,
      service_category_type: f.service_category_type,
      priority: f.priority,
      extra_charge_ids: f.extra_charge_ids,
    }).then(r => setBill(r.data)).catch(() => {});
  }, [f.package_id, f.service_category_type, f.priority, f.extra_charge_ids]);

  const isDrop = f.service_category_type === "drop";
  const isHighOrEmergency = !isDrop &&
    (f.priority === "HIGH" || f.priority === "URGENT" || f.priority === "EMERGENCY");
  // Skip the priority step entirely for drop service
  // Iter 198 — flat 3-step wizard (Type → Package → Confirm). Priority
  // moved into the Confirm step so it's shown alongside date, bill and
  // submit buttons.
  const totalSteps = 3;
  const stepLabel = (n) => ["Service type", "Package", "Confirm"][n - 1];

  const buildSrPayload = () => ({
    ...f,
    priority: isDrop ? "NORMAL" : f.priority,
    service_category: f.waste_category || "general",
    preferred_date: isHighOrEmergency ? null : f.preferred_date,
  });
  const submit = async () => {
    if (!f.package_id) { toast.error("Pick a package"); return; }
    if (!isHighOrEmergency && !f.preferred_date) { toast.error("Pick a service date"); return; }
    // Iter 201 · minimum wallet balance guard applies to Confirm too —
    // ensures the customer has enough on-account to settle the invoice
    // after service, matching Pay & Book's up-front assurance.
    if (!walletOK) {
      toast.error(
        `Minimum wallet balance of ₹${MIN_WALLET_BALANCE} required to book. `
        + `You have ₹${(walletBalance || 0).toFixed(0)}. Top up from Wallet.`
      );
      return;
    }
    setBusy(true);
    try {
      await custApi.post("/me/service-requests", buildSrPayload());
      toast.success(isHighOrEmergency
        ? "Request submitted — admin will confirm date"
        : "Request created");
      onCreated?.();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  // Step navigation flow (Iter 199): step 1 = type, 2 = package,
  // 3 = confirm (dates + priority + bill + submit).
  const goNext = () => setStep(s => Math.min(s + 1, 3));
  const goBack = () => setStep(s => Math.max(s - 1, 1));
  const canNext = (
    (step === 1 && !!f.service_category_type) ||
    (step === 2 && !!f.package_id) ||
    (step === 3)
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="bg-white sm:rounded-md w-full sm:max-w-md max-h-[95vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="mr-wizard">
        <div className="sticky top-0 bg-white p-5 border-b border-[#E5E2D9] flex items-center gap-2">
          {step > 1 && <button onClick={goBack} data-testid="mr-back" className="p-1.5 hover:bg-[#F7F6F2] rounded"><ArrowLeft size={14}/></button>}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Step {step} / {totalSteps}</div>
            <div className="font-heading text-lg">{stepLabel(step)}</div>
          </div>
          <button onClick={onClose} className="ml-auto text-[#8A8780] hover:text-[#1A1A1A]"><XCircle size={20}/></button>
        </div>

        <div className="p-5 space-y-4">
          {/* ---------- Step 1 — Service Type ---------- */}
          {step === 1 && (
            <div className="space-y-4" data-testid="mr-step-type">
              {/* Default Zone Panel */}
              {zoneSnap && (zoneSnap.zone_name || zoneSnap.lsgd_name) && (
                <div className="border-2 border-[#DC2626] bg-[#EFF3EE] rounded-md p-4" data-testid="mr-default-zone">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#DC2626] mb-1">Your Default Service Area</div>
                  <div className="font-heading text-base text-[#1A1A1A]">
                    {zoneSnap.zone_name || zoneSnap.lsgd_name}
                  </div>
                  {zoneSnap.ward_name && (
                    <div className="text-[11px] text-[#5C5A55]">
                      {zoneSnap.ward_name}{zoneSnap.lsgd_name ? ` · ${zoneSnap.lsgd_name}` : ""}
                    </div>
                  )}
                  {zoneSnap.default_drop_location && (
                    <div className="mt-2 text-[11px] text-[#5C5A55]">
                      <span className="text-[#8A8780]">Default Drop Point:</span>{" "}
                      <span className="font-medium text-[#1A1A1A]">{zoneSnap.default_drop_location.name}</span>
                      {zoneSnap.default_drop_location.start_time && (
                        <span className="text-[#8A8780]"> · {zoneSnap.default_drop_location.start_time}–{zoneSnap.default_drop_location.end_time}</span>
                      )}
                    </div>
                  )}
                  {(zoneSnap.home_service_dates?.length > 0 || zoneSnap.drop_dates?.length > 0) && (
                    <div className="mt-3 space-y-1.5">
                      {zoneSnap.home_service_dates?.length > 0 && (
                        <div>
                          <div className="text-[9px] uppercase tracking-[0.2em] text-[#8A8780]">Home Service Dates</div>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {zoneSnap.home_service_dates.slice(0, 3).map(d => (
                              <span key={d} className="text-[10px] font-mono px-2 py-0.5 bg-white border border-[#FEE2E2] rounded text-[#DC2626]">
                                {new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {zoneSnap.drop_dates?.length > 0 && (
                        <div>
                          <div className="text-[9px] uppercase tracking-[0.2em] text-[#8A8780]">Drop Dates</div>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {zoneSnap.drop_dates.slice(0, 3).map(d => (
                              <span key={d} className="text-[10px] font-mono px-2 py-0.5 bg-white border border-[#FEE2E2] rounded text-[#DC2626]">
                                {new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Service Type Cards — Swiggy-style tile picker */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: "home_service", l: "Home Service", d: "We come to your door", I: House, tone: "#DC2626", bg: "#FEE2E2" },
                  { v: "drop", l: "Drop Service", d: "Drop at our center", I: Truck, tone: "#0B2B66", bg: "#EAF0FF" },
                ].map(opt => {
                  const on = f.service_category_type === opt.v;
                  return (
                    <button key={opt.v} onClick={() => setF(s => ({ ...s, service_category_type: opt.v }))}
                            data-testid={`mr-type-${opt.v}`}
                            className={`text-left p-4 rounded-xl border-2 transition-all shadow-sm hover:shadow-md ${on ? "border-[#DC2626] ring-2 ring-[#FEE2E2]" : "border-[#E5E2D9] hover:border-[#DC2626]/40"} bg-white`}>
                      <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-2"
                            style={{ backgroundColor: opt.bg }}>
                        <opt.I size={22} weight="duotone" style={{ color: opt.tone }}/>
                      </div>
                      <div className="font-heading text-sm font-bold text-[#1A1A1A] leading-tight">{opt.l}</div>
                      <div className="text-[10px] text-[#5C5A55] mt-0.5 leading-snug">{opt.d}</div>
                      {on && (
                        <div className="mt-2 flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#DC2626] font-bold">
                          <CheckCircle size={11} weight="fill"/> Selected
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Alternate locations — drop service only */}
              {f.service_category_type === "drop" && altLocations.length > 0 && (
                <div className="bg-[#F7F6F2] border border-[#E5E2D9] rounded-md p-3" data-testid="mr-alt-locations">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Other Available Locations · next 14 days</div>
                    <span className="text-[9px] text-[#8A8780]">{altLocations.length} options</span>
                  </div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {altLocations.slice(0, 12).map((al, i) => (
                      <div key={`${al.drop_location_id}-${al.date}`}
                           className="flex items-center gap-2 px-2 py-1.5 bg-white border border-[#E5E2D9] rounded text-[11px]"
                           data-testid={`mr-alt-${i}`}>
                        <Calendar size={11} className="text-[#DC2626] shrink-0"/>
                        <span className="font-mono text-[#1A1A1A] shrink-0">
                          {new Date(al.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </span>
                        <span className="flex-1 truncate text-[#1A1A1A]">{al.name}</span>
                        {al.zone_name && <span className="text-[#8A8780] truncate">{al.zone_name}</span>}
                        {al.start_time && (
                          <span className="text-[#8A8780] shrink-0 font-mono text-[10px]">{al.start_time}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---------- Step 2 — Waste Category → Package ---------- */}
          {step === 2 && (
            <div className="space-y-3" data-testid="mr-step-package">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Waste category</div>
                <div className="flex flex-wrap gap-2">
                  {categories.map(c => (
                    <button key={c.category}
                            onClick={() => setF(s => ({ ...s, waste_category: c.category, package_id: "" }))}
                            data-testid={`mr-cat-${c.category}`}
                            className={`px-3 py-1.5 rounded-md text-xs border transition inline-flex items-center gap-1.5 ${f.waste_category === c.category ? "bg-[#DC2626] text-white border-[#DC2626]" : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                      <Recycle size={11}/>{c.category}<span className="opacity-60">· {c.types.length}</span>
                    </button>
                  ))}
                </div>
              </div>
              {f.waste_category && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Available packages</div>
                  {pkgLoading ? <div className="text-xs text-[#8A8780]">Loading…</div>
                    : packages.length === 0 ? (
                      <div className="p-4 text-center text-xs text-[#8A8780] bg-[#F7F6F2] rounded-md">
                        No packages currently include {f.waste_category}.<br/>
                        Try another category — or contact admin.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {packages.map(p => {
                          const isSelected = f.package_id === p.id;
                          const items = p.items || [];
                          const wasteItems = items.filter(i => i.kind === "waste");
                          const otherItems = items.filter(i => i.kind !== "waste");
                          const allowancesRaw = p.category_allowances || [];
                          // Iter 197.1 — real tenant packages carry
                          // allowance data inside items[] instead of
                          // category_allowances. Fall back to that
                          // shape so the feature is visible on prod data.
                          let allowances = allowancesRaw;
                          if (allowances.length === 0) {
                            const grouped = {};
                            items.forEach(i => {
                              if (i.allowed_quantity == null && i.excess_rate == null) return;
                              const key = i.category || i.name || "—";
                              if (!grouped[key]) {
                                grouped[key] = {
                                  category: key,
                                  allowed_quantity_kg: Number(i.allowed_quantity || 0),
                                  excess_rate_per_kg: Number(i.excess_rate || 0),
                                };
                              } else {
                                grouped[key].allowed_quantity_kg
                                  += Number(i.allowed_quantity || 0);
                              }
                            });
                            allowances = Object.values(grouped);
                          }
                          // Iter 197 — biohazard packing / bag note for the
                          // biomedical categories only.
                          const isBiomed = /bio[- ]?med|d[-\s]?bmw|medical/i.test(
                            `${p.name || ""} ${(p.waste_categories || []).join(" ")} ${wasteItems.map(w => w.category || "").join(" ")}`,
                          );
                          const fulfil = (p.service_category_type || "home_service")
                            .replace(/_/g, " ");
                          return (
                          <button key={p.id} onClick={() => setF(s => ({ ...s, package_id: p.id }))}
                                  data-testid={`mr-pkg-${p.id}`}
                                  className={`w-full text-left rounded-xl border-2 transition-all shadow-sm hover:shadow-md ${isSelected ? "border-[#DC2626] bg-white ring-2 ring-[#FEE2E2]" : "border-[#E5E2D9] bg-white hover:border-[#DC2626]/40"}`}>
                            {/* Header strip — package name + price, Swiggy card style */}
                            <div className={`px-4 pt-3 pb-2 rounded-t-xl relative overflow-hidden ${isSelected ? "bg-gradient-to-r from-[#FEE2E2] to-[#FFF] " : "bg-gradient-to-r from-[#F7F6F2] to-white"}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <PackageI size={18} className="text-[#DC2626] shrink-0" weight={isSelected ? "fill" : "duotone"}/>
                                  <div className="min-w-0">
                                    <div className="font-heading text-base font-bold text-[#1A1A1A] truncate">{p.name}</div>
                                    <div className="text-[10px] text-[#8A8780] mt-0.5 flex flex-wrap gap-1">
                                      <span data-testid={`mr-pkg-fulfil-${p.id}`}
                                             className="uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#EFF3EE] text-[#2D5A27] font-semibold">
                                        {fulfil}
                                      </span>
                                      {p.billing_cycle && (
                                        <span className="uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#F0EDF7] text-[#3B2C7F] font-semibold">
                                          {String(p.billing_cycle).replace(/_/g, " ")}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  <div className="font-heading text-lg font-bold text-[#1A1A1A]">
                                    ₹{Number(p.price).toFixed(0)}
                                  </div>
                                  <div className="text-[9px] uppercase tracking-[0.14em] text-[#8A8780]">Onwards</div>
                                </div>
                              </div>
                            </div>
                            {/* Body — extra-charge banner, description, inclusions table */}
                            <div className="px-4 pt-3 pb-3 space-y-3">
                              {p.description && (
                                <div className="text-[11px] text-[#5C5A55] whitespace-pre-line leading-snug"
                                      data-testid={`mr-pkg-desc-${p.id}`}>
                                  {p.description}
                                </div>
                              )}
                              {/* Iter 206 · Extra-charge banner surfaced at
                                    the TOP of the body (was buried at bottom)
                                    so pricing is transparent before the
                                    customer scrolls. Prefer the tenant-set
                                    excess-rate; fall back to the highest
                                    allowance rate. */}
                              {allowances.length > 0 && (
                                <div className="flex items-start gap-3 rounded-md border border-[#F4C7B9] bg-[#FFF7F5] px-3 py-2"
                                      data-testid={`mr-pkg-extra-banner-${p.id}`}>
                                  <div className="w-8 h-8 rounded-full bg-white border border-[#F4C7B9] flex items-center justify-center shrink-0">
                                    <CurrencyInr size={14} weight="bold" className="text-[#7A2E18]"/>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-[12px] font-semibold text-[#1A1A1A] leading-tight">Extra Charges</div>
                                    <div className="text-[10px] text-[#5C5A55] mt-0.5 leading-tight">
                                      Applicable if waste exceeds the allowed quantity
                                    </div>
                                  </div>
                                  <div className="text-[12px] font-bold text-[#DC2626] shrink-0 whitespace-nowrap">
                                    +₹{Number(Math.max(...allowances.map(a => a.excess_rate_per_kg || 0))).toFixed(2)} / kg
                                  </div>
                                </div>
                              )}
                              {(wasteItems.length > 0 || otherItems.length > 0 || allowances.length > 0) && (
                                <div className="text-[10px] text-[#5C5A55]"
                                      data-testid={`mr-pkg-items-${p.id}`}>
                                  <div className="uppercase tracking-[0.14em] text-[#8A8780] font-semibold mb-1.5">
                                    What's Included
                                  </div>
                                  {/* Iter 206 · When we have allowances, show them
                                        as a proper 3-column table (Waste Type ·
                                        Allowed Quantity · Extra Charges). Non-
                                        allowance items still render as green
                                        check bullets above the table. */}
                                  {(() => {
                                    const allowanceCategories = new Set(allowances.map(a => a.category));
                                    const bullets = [];
                                    wasteItems.forEach((i, ix) => {
                                      const label = i.name || i.category || "Waste";
                                      const category = i.category || "";
                                      if (allowanceCategories.has(category) || allowanceCategories.has(label)) return;
                                      bullets.push({ key: `w${ix}`, label, category });
                                    });
                                    otherItems.forEach((i, ix) => {
                                      bullets.push({ key: `o${ix}`, label: i.name || i.kind || "Extra", category: "" });
                                    });
                                    return (
                                      <>
                                        {bullets.length > 0 && (
                                          <ul className="space-y-0.5 mb-2">
                                            {bullets.map(b => (
                                              <li key={b.key} className="flex items-start gap-1.5">
                                                <CheckCircle size={10} weight="fill" className="text-[#2D5A27] mt-0.5 shrink-0"/>
                                                <span>
                                                  {b.category ? `Category: ${b.category}` : b.label}
                                                </span>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                        {allowances.length > 0 && (
                                          <div className="rounded-md border border-[#E5DAB7] overflow-hidden"
                                                data-testid={`mr-pkg-allowance-${p.id}`}>
                                            <table className="w-full text-[11px]">
                                              <thead className="bg-[#FBF6E0]">
                                                <tr className="text-[10px] uppercase tracking-[0.12em] text-[#7A5A11]">
                                                  <th className="text-left px-2 py-1.5 font-semibold">Waste Type</th>
                                                  <th className="text-center px-2 py-1.5 font-semibold">Allowed Quantity</th>
                                                  <th className="text-right px-2 py-1.5 font-semibold">Extra Charges</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {allowances.map((a, ai) => (
                                                  <tr key={a.category}
                                                      className={ai % 2 ? "bg-[#FDFAEF]" : "bg-white"}>
                                                    <td className="px-2 py-1.5 text-[#1A1A1A]">{a.category}</td>
                                                    <td className="px-2 py-1.5 text-center font-mono text-[#1A1A1A]">
                                                      {a.allowed_quantity_kg} kg
                                                    </td>
                                                    <td className="px-2 py-1.5 text-right font-mono text-[#DC2626] font-semibold">
                                                      +₹{Number(a.excess_rate_per_kg || 0).toFixed(2)} / kg
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                              {isBiomed && (
                                <div data-testid={`mr-pkg-biohazard-note-${p.id}`}
                                      className="flex items-start gap-2 text-[11px] text-[#7A2E18] p-3 bg-[#FEE2E2] border border-[#F4C7B9] rounded-md whitespace-pre-line leading-snug">
                                  <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0 text-[#7A2E18]"/>
                                  <div className="flex-1">
                                    <strong>Note:</strong> Biohazard bag charges and packing charges will be applicable if the required biohazard bags are not purchased from us or if the waste is not properly packed before collection.{"\n\n"}
                                    Please place your QR-coded, non-chlorinated 55-micron biohazard bag order in advance, pack the biomedical waste properly in the designated bags, and keep the waste ready for handover at the scheduled collection time.
                                  </div>
                                </div>
                              )}
                              {/* Full-width Add / Selected pill (Swiggy-style but stretched) */}
                              <div className="pt-1">
                                <div className={`w-full h-10 rounded-md flex items-center justify-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] transition-colors ${isSelected ? "bg-[#DC2626] text-white" : "border-2 border-dashed border-[#DC2626] text-[#DC2626] bg-white"}`}>
                                  {isSelected ? (
                                    <><CheckCircle size={14} weight="fill"/>Selected</>
                                  ) : "Select this package"}
                                </div>
                              </div>
                            </div>
                          </button>
                          );
                        })}
                      </div>
                    )
                  }
                </div>
              )}
            </div>
          )}

          {/* ---------- Step 3 — Confirm ---------- */}
          {step === 3 && (
            <div className="space-y-3" data-testid="mr-step-confirm">
              {/* Iter 199 — Priority selector (moved from legacy step 2). */}
              {!isDrop && (
                <div data-testid="mr-confirm-priority">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">
                    Priority
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { v: "NORMAL", l: "Normal" },
                      { v: "HIGH", l: "High" },
                      { v: "EMERGENCY", l: "Emergency" },
                    ].map(opt => {
                      const charge = charges[opt.v];
                      return (
                        <button key={opt.v} onClick={() => setF(s => ({ ...s, priority: opt.v }))}
                                 data-testid={`mr-prio-${opt.v}`}
                                 className={`p-2 rounded-md border text-center text-[11px] transition ${f.priority === opt.v ? "border-[#DC2626] bg-[#EFF3EE]" : "border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                          <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${PRIORITY_STYLE[opt.v]}`}>{opt.l}</span>
                          {charge && (
                            <div className="text-[10px] font-mono text-[#7A2E18] mt-1">
                              +₹{Number(charge.amount).toFixed(0)}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {!isHighOrEmergency ? (
                <div>
                  {/* Iter 202 · Multi-date Pay & Book banner + toggle.
                        Only shown for NORMAL priority home_service where
                        the customer has multiple regular collection
                        dates available. Multi-date bookings can ONLY be
                        paid via Razorpay Pay & Book — the wallet-only
                        `Confirm` path is hidden while the toggle is on. */}
                  {!isDrop && dateOptions.length >= 2 && (
                    <div className="mb-3 rounded-md border border-[#0B2B66]/25 bg-gradient-to-br from-[#F7F9FF] to-[#EAF0FF] p-3"
                          data-testid="mr-multi-date-banner">
                      <div className="flex items-start gap-2">
                        <div className="text-[#0B2B66] mt-0.5"><Calendar size={16}/></div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-[#0B2B66]">
                            Pay Now & Schedule Multiple Service Dates!
                          </div>
                          <div className="text-[11px] text-[#0B2B66]/80 mt-0.5 leading-snug">
                            Make your payment now and select multiple service dates in advance.
                            <br/>
                            <span className="opacity-80">No queues · Confirmed services · Hassle-free scheduling.</span>
                            <br/>
                            Plan your biomedical waste collection services ahead and enjoy a smoother experience.
                          </div>
                          <label className="mt-2 inline-flex items-center gap-2 cursor-pointer select-none"
                                  data-testid="mr-multi-date-toggle">
                            <input type="checkbox"
                                    checked={multiDateMode}
                                    onChange={(e) => {
                                      const on = e.target.checked;
                                      setMultiDateMode(on);
                                      if (on) {
                                        setMultiDates(f.preferred_date ? [f.preferred_date] : []);
                                      } else {
                                        setMultiDates([]);
                                      }
                                    }}
                                    className="h-4 w-4 accent-[#0B2B66]"/>
                            <span className="text-xs font-medium text-[#0B2B66]">
                              Enable multi-date booking (Pay & Book only)
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2 flex items-center gap-2">
                    <span>{multiDateMode ? "Pick 2 or more dates" : "Service date"}</span>
                    {isDrop && zoneSnap?.default_drop_location?.schedule_type && (
                      <span className="px-2 py-0.5 bg-[#FEE2E2] text-[#DC2626] rounded text-[9px] tracking-[0.15em]">
                        {zoneSnap.default_drop_location.schedule_type === "alternate_week" ? "Alternate Week" : "Weekly"}
                      </span>
                    )}
                    {multiDateMode && (
                      <span className="ml-auto text-[10px] font-mono text-[#0B2B66]" data-testid="mr-multi-date-count">
                        {multiDates.length} selected
                      </span>
                    )}
                  </div>
                  {(() => {
                    const dates = isDrop
                      ? (zoneSnap?.drop_dates || [])
                      : dateOptions;
                    const hasSpecial = specialDates.length > 0;
                    if (dates.length === 0 && !hasSpecial) {
                      return (
                        <div className="p-3 bg-[#F4C7B9] text-[#7A2E18] text-xs rounded-md">
                          {isDrop
                            ? "No drop point mapped to your zone yet. Please contact admin."
                            : "Your zone has no schedule mapped. Please contact admin or choose High/Emergency service."}
                        </div>
                      );
                    }
                    return (
                      <>
                        {dates.length > 0 && (
                          <div className="grid grid-cols-2 gap-2">
                            {dates.map(d => {
                              const isBooked = bookedDates.has(d);
                              const isSelected = multiDateMode
                                ? multiDates.includes(d)
                                : f.preferred_date === d;
                              const onPick = () => {
                                if (isBooked) return;
                                if (multiDateMode) {
                                  setMultiDates(cur => cur.includes(d)
                                    ? cur.filter(x => x !== d)
                                    : [...cur, d].slice(0, 6));
                                } else {
                                  setF(s => ({ ...s, preferred_date: d,
                                                 description: _stripAnyOfMarker(s.description) }));
                                }
                              };
                              return (
                                <button key={d} onClick={onPick} disabled={isBooked}
                                        data-testid={`mr-date-${d}`}
                                        title={isBooked ? "You've already booked this date" : ""}
                                        className={`p-2.5 border rounded-md text-sm flex items-center gap-2 ${
                                          isBooked
                                            ? "border-[#E5E2D9] bg-[#F7F6F2] text-[#8A8780] cursor-not-allowed line-through"
                                            : isSelected
                                              ? (multiDateMode
                                                  ? "border-[#0B2B66] bg-[#EAF0FF] text-[#0B2B66]"
                                                  : "border-[#DC2626] bg-[#EFF3EE] text-[#DC2626]")
                                              : "border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                                  {multiDateMode && !isBooked && (
                                    <span className={`h-3 w-3 rounded-sm border ${isSelected ? "bg-[#0B2B66] border-[#0B2B66]" : "border-[#8A8780]"} inline-flex items-center justify-center`}>
                                      {isSelected && <CheckCircle size={10} weight="fill" color="white"/>}
                                    </span>
                                  )}
                                  <Calendar size={12}/>{new Date(d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                                  {isBooked && <span className="ml-auto text-[9px] uppercase tracking-wider">booked</span>}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {dates.length === 0 && hasSpecial && (
                          <div className="p-3 bg-[#FBF6E0] border border-[#E5DAB7] text-[#7A5A11] text-xs rounded-md">
                            Your zone has no regular schedule — pick a Special Collection Date below,
                            or choose High/Emergency service.
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* Special Collection Days — surfaces whenever the customer's
                      zone is part of a Special Health Zone with upcoming dates,
                      in addition to (or in place of) the regular grid above. */}
                  {specialDates.length > 0 && !isDrop && (
                    <div className="mt-4 rounded-md border border-[#3B2C7F]/25 bg-[#F7F5FE] p-3"
                         data-testid="mr-special-dates">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#3B2C7F] font-semibold flex items-center gap-1 mb-2">
                        <Calendar size={11}/> Special collection days for your zone
                      </div>
                      <div className="space-y-3">
                        {specialDates.map(r => {
                          const dateList = r.dates || [];
                          const allSelected = dateList.length > 1
                            && f.preferred_date === dateList[0]
                            && (f.description || "").includes(`[Any of: ${dateList.join(", ")}]`);
                          return (
                            <div key={r.id} className="bg-white rounded border border-[#3B2C7F]/15 p-2.5">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-sm font-medium text-[#1A1A1A]">{r.title}</span>
                                {(r.special_zones || []).map(sz => (
                                  <span key={sz.id}
                                        className="text-[9px] uppercase tracking-[0.1em] bg-[#F0EDF7] border border-[#3B2C7F]/25 text-[#3B2C7F] px-1.5 py-0.5 rounded">
                                    ◆ {sz.name}
                                  </span>
                                ))}
                              </div>
                              {r.notes && (
                                <div className="text-[11px] text-[#5C5A55] mt-1 whitespace-pre-wrap">{r.notes}</div>
                              )}
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {dateList.map(d => (
                                  <button key={d}
                                          data-testid={`mr-scd-${d}`}
                                          onClick={() => setF(s => ({
                                            ...s,
                                            preferred_date: d,
                                            description: _stripAnyOfMarker(s.description),
                                          }))}
                                          className={`text-[11px] font-mono px-2 h-7 rounded border transition-colors ${f.preferred_date === d && !allSelected ? "bg-[#3B2C7F] text-white border-[#3B2C7F]" : "bg-white text-[#3B2C7F] border-[#3B2C7F]/30 hover:bg-[#F0EDF7]"}`}>
                                    {new Date(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                                  </button>
                                ))}
                              </div>
                              {dateList.length > 1 && (
                                <button type="button"
                                        data-testid={`mr-scd-any-${r.id}`}
                                        onClick={() => setF(s => {
                                          const note = `[Any of: ${dateList.join(", ")}] Admin will notify the exact collection day.`;
                                          const existing = _stripAnyOfMarker(s.description);
                                          return {
                                            ...s,
                                            preferred_date: dateList[0],
                                            description: existing ? `${existing}\n${note}` : note,
                                          };
                                        })}
                                        className={`mt-2 inline-flex items-center gap-1 text-[11px] px-2.5 h-7 rounded border transition-colors ${allSelected ? "bg-[#3B2C7F] text-white border-[#3B2C7F]" : "border-[#3B2C7F] text-[#3B2C7F] hover:bg-[#F0EDF7]"}`}>
                                  {allSelected ? "✓ Any date · admin will notify" : `Select all ${dateList.length} dates — admin will notify exact day`}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-3 bg-[#F9E4B7] border border-[#E5DAB7] rounded-md text-[11px] text-[#7A5A11] flex gap-2">
                  <Clock size={14} weight="fill"/>
                  <span>Admin will confirm the date for {f.priority} priority requests.</span>
                </div>
              )}

              {/* Extra Charges section intentionally hidden for customer flow
                  — priority-linked surcharges are shown auto-applied in the
                  Bill Summary below. */}

              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Notes (optional)</div>
                <textarea value={f.description} onChange={e => setF(s => ({ ...s, description: e.target.value }))}
                          rows={2} data-testid="mr-notes" placeholder="Any special instructions…"
                          className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
              </div>

              {/* FULL BILL */}
              {bill && (
                <div className="bg-white border-2 border-[#DC2626] rounded-md p-4" data-testid="mr-bill">
                  <div className="flex items-center gap-2 mb-2">
                    <CurrencyInr size={14} weight="fill" color="#DC2626"/>
                    <div className="font-heading text-sm">Bill Summary</div>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-[#5C5A55]">Package — {bill.package.name}</span>
                      <span className="font-mono">₹{bill.package_price.toFixed(2)}</span>
                    </div>
                    {bill.charges.map(ch => (
                      <div key={ch.id} className="flex justify-between" data-testid={`mr-bill-row-${ch.type}`}>
                        <span className="text-[#5C5A55]">
                          {ch.description}
                          {ch.auto && <span className="ml-1 text-[9px] text-[#8A8780]">(auto · {bill.priority})</span>}
                        </span>
                        <span className="font-mono">₹{ch.amount.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="border-t border-[#E5E2D9] pt-1.5 mt-1.5 flex justify-between font-semibold text-sm">
                      <span>Total</span>
                      <span className="font-mono text-[#DC2626]" data-testid="mr-bill-total">₹{bill.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white p-4 border-t border-[#E5E2D9] flex justify-between gap-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] self-center">
            Step {step} / {totalSteps}
          </div>
          {step < 3 ? (
            <button onClick={goNext} disabled={!canNext} data-testid="mr-next"
                    className="h-10 px-4 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-40 text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
              Continue<ArrowRight size={14}/>
            </button>
          ) : (
            <div className="flex flex-col gap-2 items-stretch flex-1">
              {/* Iter 201 · Wallet balance banner — always visible on
                    Step 3 so the customer sees the minimum balance
                    requirement before tapping either action. */}
              <div className={`text-[11px] rounded p-2 border flex items-center justify-between gap-2 ${
                walletLoading
                  ? "bg-[#F7F6F2] border-[#E5E2D9] text-[#5C5A55]"
                  : walletOK
                    ? "bg-[#EFF3EE] border-[#C8E6C9] text-[#1B5E20]"
                    : "bg-[#FEE2E2] border-[#F4C7B9] text-[#7A2E18]"
              }`} data-testid="mr-wallet-status">
                <span>
                  {walletLoading
                    ? "Checking wallet balance…"
                    : walletOK
                      ? `Wallet balance ₹${(walletBalance || 0).toFixed(0)} · ready to book`
                      : `Minimum ₹${MIN_WALLET_BALANCE} balance required · you have ₹${(walletBalance || 0).toFixed(0)}`}
                </span>
                {!walletLoading && !walletOK && (
                  <a href="/me/wallet"
                      data-testid="mr-wallet-topup-link"
                      className="text-[10px] uppercase tracking-wider underline font-semibold hover:no-underline">
                    Top up
                  </a>
                )}
              </div>
              <div className="text-[10px] text-[#7A2E18] bg-[#FEE2E2] border border-[#F4C7B9] rounded p-2 leading-snug"
                    data-testid="mr-confirm-excess-note">
                <strong>Note:</strong> If the quantity of waste collected exceeds
                the quantity included in the selected package, the additional
                quantity will be charged separately. The applicable
                extra-quantity charges will be added to the final bill after
                the collection is completed.
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-stretch">
                {!multiDateMode && (
                  <div className="flex-1 flex flex-col gap-1.5">
                    <button onClick={submit} disabled={busy || !walletOK} data-testid="mr-submit"
                             className="h-11 px-4 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 w-full">
                      <CheckCircle size={14} weight="bold"/>{busy ? "Submitting…" : `Confirm · ₹${(bill?.total || 0).toFixed(0)}`}
                    </button>
                    <div className="text-[10px] text-[#7A5A11] flex items-start gap-1 leading-snug"
                          data-testid="mr-confirm-hint">
                      <WarningCircle size={11} weight="fill" className="mt-0.5 shrink-0 text-[#B45309]"/>
                      <span>Service confirmation is subject to slot availability.</span>
                    </div>
                  </div>
                )}
                <div className="flex-1 flex flex-col gap-1.5">
                  <button onClick={submitAndPay}
                           disabled={busy || !walletOK || (multiDateMode && multiDates.length < 2)}
                           data-testid="mr-pay-book"
                           title={multiDateMode && multiDates.length < 2
                             ? "Pick at least 2 dates for multi-date booking"
                             : "Pay via Razorpay and reserve your slot now"}
                           className="h-11 px-4 bg-[#0B2B66] hover:bg-[#081E4A] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 w-full">
                    {busy ? "Redirecting…"
                      : multiDateMode
                        ? `Pay & Book · ${multiDates.length} ${multiDates.length === 1 ? "date" : "dates"} · ₹${(((bill?.total || 0)) * multiDates.length).toFixed(0)}`
                        : `Pay & Book · ₹${(bill?.total || 0).toFixed(0)}`}
                  </button>
                  <div className="text-[10px] text-[#1B4E99] flex items-start gap-1 leading-snug"
                        data-testid="mr-pay-book-hint">
                    <ShieldCheck size={11} weight="fill" className="mt-0.5 shrink-0 text-[#0B2B66]"/>
                    <span>Secure your pickup in advance to avoid delays and ensure timely service.</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
